/**
 * Consent-evidence PDF builder (feat-022-consent-evidence, ADR 0001 §9).
 *
 * Produces a subpoena-grade, downloadable PDF for one location: a header with
 * tenant + location + timezone, generated-at (location-local, labeled) and
 * generated-by, one row per employee (name, consent, consent-at, onboarding,
 * training), and an "N of M consented" summary.
 *
 * `@react-pdf/renderer` is heavy, so it is **lazy-imported** inside
 * `generateConsentEvidencePdf` (dynamic `import()`) and never pulled into the
 * initial bundle. Employee names are rendered as `<Text>` children — there is
 * no `dangerouslySetInnerHTML` anywhere in this module (spec C5b); @react-pdf
 * has no HTML sink, so names are inert text nodes.
 *
 * All timestamps come from the report as UTC ISO-8601 and are localized to the
 * location timezone with an explicit label via `utils/locationTime`
 * (spec D8 / ADR §9). Null `location.timezone` falls back to UTC, labeled UTC.
 */
import { createElement } from 'react';
import type {
  ConsentReport,
  ConsentReportServer,
  ServerEventEntry,
} from '../../services/consentEvidenceService';
import { formatLocationTime, formatLocationTimeParts } from '../../utils/locationTime';

/** A presentation-ready employee row — pure, derived from the report payload. */
export interface ConsentReportRow {
  name: string;
  consent: 'Yes' | 'No';
  /** Location-local labeled timestamp, or '—' when consent not recorded. */
  consentAt: string;
  onboarding: 'Yes' | 'No';
  training: 'Yes' | 'No';
}

const yesNo = (v: boolean): 'Yes' | 'No' => (v ? 'Yes' : 'No');

/**
 * Map a report server to a display row, localizing `consent_given_at` to the
 * location timezone. Exported (and pure) so it can be unit-tested without the
 * @react-pdf renderer.
 */
export function buildReportRow(
  server: ConsentReportServer,
  timezone: string | null,
): ConsentReportRow {
  return {
    name: server.name,
    consent: yesNo(server.consent_given),
    // Only consenters have a meaningful timestamp; '—' for everyone else.
    consentAt:
      server.consent_given && server.consent_given_at
        ? formatLocationTime(server.consent_given_at, timezone, '—')
        : '—',
    onboarding: yesNo(server.onboarding_complete),
    training: yesNo(server.training_complete),
  };
}

export function buildReportRows(report: ConsentReport): ConsentReportRow[] {
  const tz = report.location.timezone;
  return report.servers.map((s) => buildReportRow(s, tz));
}

// ---------- Event-history appendix (feat-024, ADR 0001 §D4) ----------

/**
 * Human-readable label for each of the six `server_event` types (ADR D4, closed
 * map). Unrecognized values pass through verbatim — a safe inert fallback that
 * never throws on a legal document, and the set is closed server-side anyway.
 */
const EVENT_TYPE_LABELS: Record<ServerEventEntry['event_type'], string> = {
  consent_given: 'Consent given',
  consent_withdrawn: 'Consent withdrawn',
  onboarding_complete: 'Onboarding completed',
  onboarding_reset: 'Onboarding reset',
  training_complete: 'Training completed',
  training_reset: 'Training reset',
};

export function eventTypeLabel(eventType: string): string {
  return EVENT_TYPE_LABELS[eventType as ServerEventEntry['event_type']] ?? eventType;
}

/**
 * Role/source label derived from `actor_type` (ADR D4, closed set). The payload
 * carries no email/`actor_id`, so this is the only actor representation — and it
 * must never become one. Unrecognized values map to 'System' (safe generic).
 */
export function actorLabel(actorType: string): string {
  switch (actorType) {
    case 'server':
      return 'Self-service';
    case 'management':
      return 'Manager';
    case 'system':
      return 'System';
    default:
      return 'System';
  }
}

/** A presentation-ready event-history row — pure, derived from one event entry. */
export interface EventHistoryRow {
  /** Human-readable event type label. */
  type: string;
  /** `actor_type` as a role/source label — never an email. */
  recordedBy: string;
  /** Local wall-clock + zone abbreviation, e.g. `2026-05-12 08:55 AM PST`. */
  timeMain: string;
  /** Explicit UTC offset, e.g. `(UTC−08:00)`; rendered muted. '' if unparseable. */
  timeOffset: string;
  /** True when `source === 'backfill'` → renders the "Backfilled" marker. */
  backfilled: boolean;
  /** Withdrawal reason — present ONLY on `consent_withdrawn` rows that carry one. */
  reason: string | null;
}

/**
 * Map one event entry to a display row. The timestamp is localized to the
 * location timezone with an explicit DST-correct offset via `locationTime.ts`
 * (ADR D4); the offset is split out so it can be rendered muted, as in the
 * approved mockup. Pure + exported so the mapping can be unit-tested without the
 * @react-pdf renderer.
 */
export function buildEventRow(
  event: ServerEventEntry,
  timezone: string | null,
): EventHistoryRow {
  const parts = formatLocationTimeParts(event.occurred_at, timezone);
  return {
    type: eventTypeLabel(event.event_type),
    recordedBy: actorLabel(event.actor_type),
    timeMain: parts ? `${parts.dateTime} ${parts.abbreviation}` : event.occurred_at || '—',
    timeOffset: parts ? `(${parts.offsetLabel})` : '',
    backfilled: event.source === 'backfill',
    // Reason is shown only on withdrawals (kickoff decision #4 / ADR D4).
    reason: event.event_type === 'consent_withdrawn' && event.reason ? event.reason : null,
  };
}

/**
 * Build the event-history rows for one employee, in the ASC order the backend
 * already returns. A missing/undefined `event_history` is treated as `[]`
 * (defensive against an old backend and the zero-events case — ADR D4).
 */
export function buildEventRows(
  server: ConsentReportServer,
  timezone: string | null,
): EventHistoryRow[] {
  return (server.event_history ?? []).map((e) => buildEventRow(e, timezone));
}

/** "0 events" / "1 event" / "N events" — the count chip in the employee band. */
export function eventCountLabel(n: number): string {
  return `${n} event${n === 1 ? '' : 's'}`;
}

/** `consent-evidence_<location>_<YYYY-MM-DD>.pdf`, slugged + safe for disk. */
export function consentEvidenceFilename(report: ConsentReport): string {
  const slug = (report.location.name || 'location')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'location';
  // Date portion of the generated_at UTC stamp (stable, no locale surprises).
  const date = (report.generated_at || '').slice(0, 10) || 'report';
  return `consent-evidence_${slug}_${date}.pdf`;
}

// ---------- PDF document (built only after the lazy import resolves) ----------

// The @react-pdf module is only ever loaded via dynamic import(), so its
// primitives are typed loosely here — its published types model the namespace
// as a self-referential default export that does not satisfy `typeof import()`.
type ReactPdf = any;

const COLS = [
  { key: 'name', label: 'Employee', flex: 3 },
  { key: 'consent', label: 'Consent', flex: 1 },
  { key: 'consentAt', label: 'Consent date/time', flex: 4 },
  { key: 'onboarding', label: 'Onboarding', flex: 1.4 },
  { key: 'training', label: 'Training', flex: 1.4 },
] as const;

/**
 * Construct the @react-pdf element tree. Takes the dynamically-imported module
 * so nothing here is statically linked to @react-pdf. Names + all cell values
 * are passed as `Text` children (text nodes only).
 */
function buildDocument(ReactPDF: ReactPdf, report: ConsentReport) {
  const { Document, Page, View, Text, StyleSheet } = ReactPDF;
  const tz = report.location.timezone;

  const styles = StyleSheet.create({
    page: { padding: 36, fontSize: 9, color: '#111827', fontFamily: 'Helvetica' },
    title: { fontSize: 15, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
    subtitle: { fontSize: 10, color: '#374151', marginBottom: 1 },
    meta: { fontSize: 8, color: '#6b7280', marginBottom: 1 },
    summary: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginTop: 10, marginBottom: 8 },
    headerRule: { borderBottomWidth: 1, borderBottomColor: '#111827', marginTop: 8 },
    row: {
      flexDirection: 'row',
      borderBottomWidth: 0.5,
      borderBottomColor: '#e5e7eb',
      paddingVertical: 4,
    },
    headRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#9ca3af',
      paddingVertical: 4,
    },
    cell: { paddingRight: 6 },
    headCell: { paddingRight: 6, fontFamily: 'Helvetica-Bold', fontSize: 8, color: '#374151' },
    footer: { position: 'absolute', bottom: 20, left: 36, right: 36, fontSize: 7, color: '#9ca3af' },

    // ----- Appendix A — Event History (feat-024) -----
    appendixTitle: { fontSize: 13, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
    appendixIntro: { fontSize: 8, color: '#6b7280', marginBottom: 12, lineHeight: 1.5 },
    emp: { marginBottom: 12 },
    // [NEW] subtle gray employee subheader band (mockup token --band #f3f4f6).
    empBand: {
      flexDirection: 'row',
      alignItems: 'baseline',
      backgroundColor: '#f3f4f6',
      paddingVertical: 4,
      paddingHorizontal: 6,
    },
    empName: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#111827' },
    empCount: { fontSize: 8, color: '#6b7280', marginLeft: 8 },
    empEmpty: { fontSize: 8.5, color: '#6b7280', fontStyle: 'italic', paddingVertical: 5 },
    evCellEvent: { paddingRight: 6, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' },
    tsOffset: { color: '#6b7280' },
    reasonLabel: { color: '#6b7280' },
    // [NEW] backfilled marker chip (mockup token --chip-border #d1d5db).
    chip: {
      marginLeft: 6,
      paddingHorizontal: 3,
      paddingVertical: 0.5,
      borderWidth: 0.5,
      borderColor: '#d1d5db',
      borderRadius: 2,
      fontSize: 6.5,
      fontFamily: 'Helvetica-Bold',
      color: '#6b7280',
    },
  });

  const rows = buildReportRows(report);
  const generatedAt = formatLocationTime(report.generated_at, tz, report.generated_at || '');
  const tzLabel = tz || 'UTC';

  const headerCells = COLS.map((c) =>
    createElement(Text, { key: c.key, style: [styles.headCell, { flex: c.flex }] }, c.label),
  );

  const bodyRows = rows.map((r, i) =>
    createElement(
      View,
      { key: report.servers[i]?.id || `row-${i}`, style: styles.row },
      COLS.map((c) =>
        createElement(
          Text,
          { key: c.key, style: [styles.cell, { flex: c.flex }] },
          r[c.key],
        ),
      ),
    ),
  );

  // ----- Appendix A — Event History (feat-024) -----
  // Per-employee chronological history. Column flex weights mirror the approved
  // mockup (Event 3 · Date/time 4 · Recorded by 2 · Details 3).
  const EV_COLS = [
    { key: 'event', label: 'Event', flex: 3 },
    { key: 'time', label: 'Date / time', flex: 4 },
    { key: 'by', label: 'Recorded by', flex: 2 },
    { key: 'details', label: 'Details', flex: 3 },
  ] as const;

  const empBlocks = report.servers.map((srv, si) => {
    const evRows = buildEventRows(srv, tz);

    const band = createElement(
      View,
      { key: 'band', style: styles.empBand },
      createElement(Text, { key: 'name', style: styles.empName }, srv.name),
      createElement(Text, { key: 'count', style: styles.empCount }, eventCountLabel(evRows.length)),
    );

    if (evRows.length === 0) {
      return createElement(
        View,
        { key: srv.id || `emp-${si}`, style: styles.emp },
        band,
        createElement(Text, { key: 'empty', style: styles.empEmpty }, 'No recorded events.'),
      );
    }

    const head = createElement(
      View,
      { key: 'head', style: styles.headRow },
      ...EV_COLS.map((c) =>
        createElement(Text, { key: c.key, style: [styles.headCell, { flex: c.flex }] }, c.label),
      ),
    );

    const eventRows = evRows.map((r, ri) =>
      createElement(
        View,
        { key: `${srv.id || si}-${ri}`, style: styles.row },
        // Event: label + optional "Backfilled" chip — both inert text nodes.
        createElement(
          View,
          { key: 'event', style: [styles.evCellEvent, { flex: 3 }] },
          createElement(Text, { key: 'label' }, r.type),
          r.backfilled ? createElement(Text, { key: 'chip', style: styles.chip }, 'BACKFILLED') : null,
        ),
        // Date/time: human-readable wall-clock + muted explicit UTC offset.
        createElement(
          Text,
          { key: 'time', style: [styles.cell, { flex: 4 }] },
          r.timeMain,
          r.timeOffset ? createElement(Text, { key: 'off', style: styles.tsOffset }, ` ${r.timeOffset}`) : null,
        ),
        // Recorded by: role/source label only — never an email (ADR D3/D4).
        createElement(Text, { key: 'by', style: [styles.cell, { flex: 2 }] }, r.recordedBy),
        // Details: withdrawal reason (inert), or em-dash.
        r.reason
          ? createElement(
              Text,
              { key: 'details', style: [styles.cell, { flex: 3 }] },
              createElement(Text, { key: 'rl', style: styles.reasonLabel }, 'Reason: '),
              r.reason,
            )
          : createElement(Text, { key: 'details', style: [styles.cell, { flex: 3 }] }, '—'),
      ),
    );

    return createElement(
      View,
      { key: srv.id || `emp-${si}`, style: styles.emp },
      band,
      head,
      ...eventRows,
    );
  });

  const appendixIntro =
    'Complete chronological audit trail for each employee (earliest first). Every consent, ' +
    'onboarding, and training event is shown without omission. Timestamps are in the location ' +
    'local timezone with explicit UTC offset. Events recorded retroactively during data ' +
    'migration are marked "Backfilled".';
  const appendixFooter =
    'Append-only audit trail · timestamps shown in the location local timezone with UTC offset.' +
    '  ·  Appendix A — Event History';

  return createElement(
    Document,
    { title: `Consent evidence — ${report.location.name}` },
    createElement(
      Page,
      { size: 'A4', style: styles.page },
      // ----- Header -----
      createElement(Text, { style: styles.title }, 'Consent Evidence Report'),
      createElement(Text, { style: styles.subtitle }, report.tenant.name),
      createElement(
        Text,
        { style: styles.subtitle },
        `${report.location.name}  ·  Timezone: ${tzLabel}`,
      ),
      createElement(Text, { style: styles.meta }, `Generated: ${generatedAt}`),
      createElement(
        Text,
        { style: styles.meta },
        `Generated by: ${report.generated_by.email} (${report.generated_by.role})`,
      ),
      createElement(View, { style: styles.headerRule }),
      // ----- Summary -----
      createElement(
        Text,
        { style: styles.summary },
        `${report.summary.consented} of ${report.summary.total} employees consented to being recorded`,
      ),
      // ----- Table -----
      createElement(View, { style: styles.headRow }, ...headerCells),
      ...bodyRows,
      // ----- Footer -----
      createElement(
        Text,
        { style: styles.footer, fixed: true },
        'Append-only audit trail · timestamps shown in the location local timezone with UTC offset.',
      ),
    ),
    // ----- Page 2+ : Appendix A — Event History (feat-024) -----
    // A separate page; the current-status page above is unchanged (kickoff
    // decision #2). @react-pdf paginates this page automatically when the
    // (uncapped) history overflows.
    createElement(
      Page,
      { size: 'A4', style: styles.page },
      createElement(Text, { style: styles.appendixTitle }, 'Appendix A — Event History'),
      createElement(Text, { style: styles.appendixIntro }, appendixIntro),
      ...empBlocks,
      createElement(Text, { style: styles.footer, fixed: true }, appendixFooter),
    ),
  );
}

/**
 * Build the PDF for a location report and trigger a browser download. Lazily
 * loads @react-pdf/renderer so it stays out of the main bundle.
 *
 * @returns the generated filename (handy for tests / toasts).
 */
export async function generateConsentEvidencePdf(report: ConsentReport): Promise<string> {
  const ReactPDF = await import('@react-pdf/renderer');
  const blob = await ReactPDF.pdf(buildDocument(ReactPDF, report)).toBlob();
  const filename = consentEvidenceFilename(report);

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke on the next tick so the click-driven download has the URL.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  return filename;
}
